import {
  InvoiceDetailDoc,
  InvoicePushTarget,
  InvoiceTemplate,
  KakaoAlimtalkRecipientItem,
} from '@gooduncles/gu-app-schema';
import { Button, Popconfirm, notification } from 'antd';
import dayjs from 'dayjs';
import { chunk, uniq } from 'lodash-es';
import { FC, useCallback, useEffect, useState } from 'react';
import { map } from 'rxjs';
import { isProduction, senderKey } from 'src/constants';

import { errorObjectToString, formatNumber, sleep } from 'src/utils/common';
import { dayjsesToStringDateWhere, formatDate, getDefaultDate } from 'src/utils/date-util';
import { callSendInvoicePush, callSendKakaoAlimTalkForInvoice } from 'src/utils/firebase/firebase-callable';
import {
  batchEnd,
  batchStart,
  deleteInvoiceDetail,
  getAlimtalkMessage,
  getUsers,
  observeInvoiceDetail,
} from 'src/utils/firebase/firebase-short-cut';
import { organizeKakaotalkResultMessage } from 'src/utils/nhn-notification-util';
import { encryptInvoiceId } from 'src/utils/payment-util';

import DatePickerWithArrows from 'src/components/Common/DatePickerWithArrows/DatePickerWithArrows';
import Loading from 'src/components/Loading/Loading';
import InvoiceDetailTable from 'src/components/NewSettlement/InvoiceTable/InvoiceDetailTable';
import SendInvoiceModal, { SendInvoiceSelection } from './components/SendInvoiceModal';

const defaultValue = getDefaultDate();

/**
 * 카카오톡 알림톡을 발송하고 해당 row를 업데이트한다.
 */
const onSendKakaoAlimTalk = async (recipientList: KakaoAlimtalkRecipientItem<InvoiceTemplate>[]) => {
  try {
    // 1. 카카오톡 전송
    if (!senderKey) {
      throw new Error('senderKey가 없습니다.');
    }

    const result = await callSendKakaoAlimTalkForInvoice({
      senderKey,
      recipientList,
      templateCode: 'invoice',
    });

    if (result.data.result !== 'success') {
      throw new Error(`카카오톡 전송 요청에 실패했습니다. ${result.data.reason ?? '알 수 없는 오류'}`);
    }

    if (!result.data.alimtalkMessageDocId) {
      throw new Error('요청은 성공했으나, ID를 받지 못했습니다.');
    }

    const alimtalkMessageDoc = await getAlimtalkMessage<InvoiceTemplate>(result.data.alimtalkMessageDocId);
    if (!alimtalkMessageDoc) {
      throw new Error('요청은 성공했으나, 메시지 정보를 받지 못했습니다.');
    }

    // 요청 결과를 메시지로 정리한다.
    const description = organizeKakaotalkResultMessage(alimtalkMessageDoc.recipientWithResultList);

    notification.success({
      message: '전송 요청 성공',
      description,
      className: 'pre-line-notification',
    });
  } catch (error) {
    console.error(error);
    const errorMessage = errorObjectToString(error);
    notification.error({
      message: '정산 목록 전송에 실패했습니다.',
      description: errorMessage,
    });
  }
};

/**
 * 선택한 행을 카카오 알림톡(v1 fallback)으로 발송한다.
 * 토큰(encryptInvoiceId)·base 도메인·20개 chunk + 300ms 딜레이 패턴 그대로 유지한다.
 */
const sendAlimtalkForRows = async (rows: InvoiceDetailDoc[]) => {
  const recipientList = rows
    .filter((row) => row.recipientInfo.userTel.length > 8)
    .map((invoiceDetail) => {
      const encryptedInvoiceId = encryptInvoiceId(invoiceDetail._id);
      const alimtalkRecipientItem: KakaoAlimtalkRecipientItem<InvoiceTemplate> = {
        recipientNo: invoiceDetail.recipientInfo.userTel,
        templateParameter: {
          date: formatDate(invoiceDetail.date, 'yy년 L월 d일(EEEEEE)'),
          store: invoiceDetail.recipientInfo.name,
          amount: formatNumber(invoiceDetail.invoiceAmount),
          base: isProduction ? 'commerce-gooduncles.web.app/invoice' : 'commerce-gooduncles-dev.web.app/invoice',
          invoice: encryptedInvoiceId,
        },
        recipientGroupingKey: invoiceDetail._id,
      };
      return alimtalkRecipientItem;
    });

  if (recipientList.length === 0) {
    return;
  }

  // 대량 발송의 경우 간헐적으로 콜백을 받지 못하는 에러가 발생하여 20개씩 나눠서 보낸다.
  const chunkedRecipientList = chunk(recipientList, 20);
  for (const chunkedRecipientListItem of chunkedRecipientList) {
    await onSendKakaoAlimTalk(chunkedRecipientListItem);
    // 정상 콜백 처리를 위해 연달아 발송시 300ms 딜레이를 준다.
    await sleep(300);
  }
};

/**
 * v2 전환(serviceVersion === 2) 매장에 청구서 도착 푸시를 발송한다.
 * 딥링크 타깃은 functions가 평문 invoiceDetail._id로 `/invoices/{id}`를 조립하므로 토큰 불필요.
 */
const onSendInvoicePush = async (rows: InvoiceDetailDoc[]) => {
  if (rows.length === 0) {
    return;
  }
  try {
    const targets: InvoicePushTarget[] = rows.map((invoiceDetail) => ({
      storeId: invoiceDetail.storeId,
      invoiceDetailId: invoiceDetail._id,
      title: '청구서가 도착했어요',
      body: `${formatDate(invoiceDetail.date, 'L월 d일')} 청구 ${formatNumber(invoiceDetail.invoiceAmount)}원`,
    }));

    const result = await callSendInvoicePush({ targets });

    if (result.data.result !== 'success') {
      throw new Error(`푸시 발송 요청에 실패했습니다. ${result.data.reason ?? '알 수 없는 오류'}`);
    }

    const { stats, failures } = result.data;
    const description = [
      stats ? `전체 ${stats.total}건 · 성공 ${stats.success}건 · 실패 ${stats.fail}건` : '',
      failures && failures.length > 0 ? failures.map((f) => `❌ ${f.storeId}: ${f.reason}`).join('\n') : '',
    ]
      .filter(Boolean)
      .join('\n');

    notification.success({
      message: '푸시 발송 요청 성공',
      description,
      className: 'pre-line-notification',
    });
  } catch (error) {
    console.error(error);
    notification.error({
      message: '푸시 발송에 실패했습니다.',
      description: errorObjectToString(error),
    });
  }
};

const handleDeleteInvoiceDetail = async (rows: InvoiceDetailDoc[]) => {
  try {
    batchStart();
    for (const row of rows) {
      await deleteInvoiceDetail(row._id, { bBatch: true });
    }
    await batchEnd();
    notification.success({ message: '삭제 성공' });
  } catch (error) {
    console.error(error);
    const errorMessage = errorObjectToString(error);
    notification.error({
      message: '삭제에 실패했습니다.',
      description: errorMessage,
    });
  }
};

const InvoiceDetailPage: FC = () => {
  const [date, setDate] = useState<dayjs.Dayjs | null>(defaultValue);
  const [rowData, setRowData] = useState<InvoiceDetailDoc[]>([]);
  const [selectedRows, setSelectedRows] = useState<InvoiceDetailDoc[]>([]);
  const [conversionMap, setConversionMap] = useState<Record<string, boolean>>({});
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onDeleteInvoiceDetail = useCallback(async () => {
    if (selectedRows.length === 0) {
      notification.info({ message: '삭제할 청구서를 선택해주세요.' });
      return;
    }
    setLoading(true);
    await handleDeleteInvoiceDetail(selectedRows);
    setSelectedRows([]);
    setLoading(false);
  }, [selectedRows]);

  const openSendModal = useCallback(() => {
    if (selectedRows.length === 0) {
      notification.info({ message: '청구서를 선택해주세요.' });
      return;
    }
    setSendModalOpen(true);
  }, [selectedRows]);

  const handleSend = useCallback(async ({ pushRows, alimtalkRows }: SendInvoiceSelection) => {
    // 채널은 모달에서 행별로 정해진다. 여기선 받은 대로 각 경로에 발송한다.
    setLoading(true);
    try {
      await onSendInvoicePush(pushRows);
      await sendAlimtalkForRows(alimtalkRows);
    } finally {
      setLoading(false);
      setSendModalOpen(false);
      setSelectedRows([]);
    }
  }, []);

  // 선택과 무관하게 화면에 표시된 청구서 매장들의 v2 전환 상태를 미리 조회한다.
  useEffect(() => {
    const storeIds = uniq(rowData.map((row) => row.storeId));
    if (storeIds.length === 0) {
      setConversionMap({});
      return;
    }

    let cancelled = false;
    (async () => {
      // Firestore 'in' 쿼리 제한(최대 30)에 맞춰 청크로 user를 조회한다.
      const userChunks = await Promise.all(chunk(storeIds, 30).map((ids) => getUsers([['storeId', 'in', ids]])));
      const map: Record<string, boolean> = {};
      userChunks.flat().forEach((user) => {
        // 매장에 v2 전환 user가 한 명이라도 있으면 푸시 가능으로 본다.
        if (user.serviceVersion === 2) {
          map[user.storeId] = true;
        }
      });
      if (!cancelled) {
        setConversionMap(map);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rowData]);

  useEffect(() => {
    if (date) {
      setLoading(true);
      const rightDateForADay = dayjs(formatDate(date.toDate(), "yyyy-MM-dd'T'23:59:59+0900"));
      const [leftDate, rightDate] = dayjsesToStringDateWhere(date, rightDateForADay);
      const subscription = observeInvoiceDetail([], {
        sortKey: 'date',
        orderBy: 'desc',
        startValue: leftDate,
        endValue: rightDate,
      })
        .pipe(map((docs) => docs.filter((d) => d.deletedAt === null)))
        .subscribe((docs) => {
          setRowData(docs);
          setLoading(false);
        });
      return () => subscription.unsubscribe();
    }
  }, [date]);

  return (
    <>
      {loading && <Loading title='잠시만 기다려주세요.' />}
      <div className='tabBody flexColumn height100'>
        <section className='flexRow flexSpaceBetween'>
          <div className='flexRow'>
            <DatePickerWithArrows date={date} setDate={setDate} />
          </div>
          <div className='flexRow'>
            <Popconfirm title='삭제를 진행하시겠습니까?' onConfirm={onDeleteInvoiceDetail} okButtonProps={{ loading }}>
              <Button disabled={selectedRows.length <= 0} danger loading={loading}>
                {`${selectedRows.length}개 삭제`}
              </Button>
            </Popconfirm>
            <Button type='primary' disabled={selectedRows.length <= 0} loading={loading} onClick={openSendModal}>
              {`${selectedRows.length}개 청구서 발송`}
            </Button>
          </div>
        </section>
        <section className='height100'>
          <InvoiceDetailTable rowData={rowData} setSelectedRows={setSelectedRows} conversionMap={conversionMap} />
        </section>
      </div>
      <SendInvoiceModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        selectedRows={selectedRows}
        conversionMap={conversionMap}
        loading={loading}
        onSend={handleSend}
      />
    </>
  );
};

export default InvoiceDetailPage;
